import { redirect } from 'react-router';
import { store } from '@/redux/store';
import { restoreSession } from '@/utils/tokenRefresh';
import { userApi } from '@/redux/api/userApi';
import { PATHS } from '@/navigation/paths';
import { withReturnTo } from '@/navigation/utils/returnTo';
import { passesIdGateOnProfile } from '@gearghost/api-schemas/user';
import { createLogger } from '@/utils/logger';

const logger = createLogger('createAuthLoader');

/**
 * Build the login redirect target, preserving the attempted path as a
 * `?returnTo=` param so the login page can send the user back after auth.
 *
 * Returns the bare login path when there's no meaningful destination to
 * preserve — no request (some loaders call with no args), or the user was
 * headed to `/` (home is the default post-login landing, so a returnTo there
 * is just noise). The path is captured WITHOUT origin (pathname + search +
 * hash only) so we never round-trip an absolute URL through the query string.
 *
 * @param {Request} [request] - the loader's Fetch Request (has the attempted URL)
 * @returns {string} login path, optionally with ?returnTo=
 */
export const loginWithReturnTo = (request) => {
  if (!request?.url) return PATHS.login;
  const url = new URL(request.url);
  const dest = url.pathname + url.search + url.hash;
  if (dest === '/' || url.pathname === PATHS.login) return PATHS.login;
  return withReturnTo(PATHS.login, dest);
};

/**
 * Try to ensure we have a valid token before proceeding: an expired one is
 * renewed from the session cookie (restoreSession).
 *
 * Returns false (causing redirect) only if:
 * 1. This browser never signed in (true guest)
 * 2. The backend refused the session (401/403: it expired or was revoked)
 *
 * An unreachable backend does NOT cause redirect - let the page load, and
 * the axios interceptor retries its requests.
 */
const ensureValidToken = async () => (await restoreSession()) !== 'signed-out';

/**
 * Read one no-argument RTK Query endpoint for a route's checks, from the
 * cache when it holds an answer. The read resolves with `error` rather than
 * rejecting; a failed one leaves the checks without their answer (null): the
 * page loads and its own requests report the failure, and the backend still
 * refuses whatever the person may not do.
 *
 * The profile below is read this way, and so is a store's standing on the
 * vendor terms (storeLoader).
 *
 * @param {{ initiate: Function }} endpoint - e.g. userApi.endpoints.getProfile
 * @param {string} what - what was being read, for the log line
 * @returns {Promise<any|null>} the endpoint's data, or null when the read failed
 */
export const readForRouteChecks = async (endpoint, what) => {
  const promise = store.dispatch(
    endpoint.initiate(undefined, { forceRefetch: false })
  );

  try {
    const result = await promise;
    if (result.error) {
      logger.warn({ err: result.error }, `${what} for the route checks failed; the page loads without them`);
      return null;
    }
    return result.data;
  } finally {
    promise.unsubscribe();
  }
};

/** The signed-in person's profile, for the role and verification checks. */
const fetchUserProfile = () => readForRouteChecks(userApi.endpoints.getProfile, 'profile');

/**
 * The app's ID gate for a signed-in person, which the backend decides
 * (GET user/verification's idGate, Tyler 2026-10-22: "The gates should
 * check with stripe. They shouldn't ask the frontend"): a verified ID
 * passes on the profile alone, so most people cost no second read; anyone
 * else gets the backend's word, which lets in a check Stripe is still
 * reviewing. Null when that read failed, as readForRouteChecks answers.
 *
 * @param {Object} profile - the person's profile (getProfile's data)
 * @returns {Promise<'pass'|'verify'|null>}
 */
export const readIdGate = async (profile) => {
  if (passesIdGateOnProfile(profile)) return 'pass';
  const verifications = await readForRouteChecks(userApi.endpoints.getVerifications, 'verification status');
  return verifications === null ? null : verifications.idGate;
};

/**
 * Loader configuration options
 * @typedef {Object} LoaderOptions
 * @property {boolean} [requireAuth=true] - Require valid token
 * @property {boolean} [requireIdGate=false] - Require the app's ID gate to
 *   let the person in (readIdGate): a verified ID, or one Stripe is still reviewing
 * @property {boolean} [requireVerification=false] - Require a verified ID
 *   (checkout and the pages that wait for the verdict)
 * @property {string|null} [requireRole=null] - Require specific role (e.g., 'vendor')
 * @property {boolean} [guestOnly=false] - Redirect if user IS authenticated (for login/signup pages)
 * @property {boolean} [fetchUser=true] - Whether to fetch and return user profile
 */

/**
 * Creates a configurable auth loader for React Router.
 *
 * @param {LoaderOptions} options - Configuration options
 * @returns {Function} A loader function for React Router
 *
 * @example
 * // Basic auth (just requires login)
 * const authLoader = createAuthLoader({ requireAuth: true });
 *
 * @example
 * // Requires login + ID verification
 * const verificationLoader = createAuthLoader({
 *   requireAuth: true,
 *   requireVerification: true
 * });
 *
 * @example
 * // Requires login + ID verification + vendor role
 * const vendorLoader = createAuthLoader({
 *   requireAuth: true,
 *   requireVerification: true,
 *   requireRole: 'vendor'
 * });
 *
 * @example
 * // Guest-only pages (login, signup)
 * const guestLoader = createAuthLoader({
 *   requireAuth: false,
 *   guestOnly: true,
 *   fetchUser: false
 * });
 */
export const createAuthLoader = (options = {}) => {
  const {
    requireAuth = true,
    requireIdGate = false,
    requireVerification = false,
    requireRole = null,
    guestOnly = false,
    fetchUser = true,
  } = options;

  // All redirects use `return` instead of `throw` to avoid briefly
  // flashing the route's errorElement while React Router processes
  // the redirect. Thrown responses go through the error path;
  // returned responses are handled as normal navigation.

  return async ({ request } = {}) => {
    // Guest-only mode: redirect authenticated users away. A signed-in
    // person opening /login in a new tab holds no token yet (it lives in
    // memory), so the session is asked for first.
    if (guestOnly) {
      if (await restoreSession() === 'signed-in') {
        return redirect(PATHS.dashboard.orders);
      }
      return null;
    }

    // Auth check
    if (requireAuth) {
      const isValid = await ensureValidToken();
      if (!isValid) {
        // Preserve where the user was trying to go so login can send them
        // back after authenticating (e.g. a shared-cart link clicked while
        // logged out). The login page reads `?returnTo=`; we only attach it
        // for a real in-app destination, never for `/` (home is the default
        // post-login landing anyway). A loader redirect is a URL-string
        // redirect and can't carry router `location.state`, so the path goes
        // on the query string — the login component honors both.
        return redirect(loginWithReturnTo(request));
      }
    }

    // Fetch user profile if needed
    const user = fetchUser ? await fetchUserProfile() : null;

    // Role check (if required)
    if (requireRole && user) {
      if (user.role !== requireRole) {
        return redirect(PATHS.dashboard.orders);
      }
    }

    // The app's ID gate (if required). A failed read lets the page load,
    // as a failed profile read does; the backend still refuses what the
    // person may not do.
    if (requireIdGate && user) {
      if ((await readIdGate(user)) === 'verify') {
        return redirect(PATHS.verifyId);
      }
    }

    // A verified ID (if required)
    if (requireVerification && user) {
      if (!user.verification?.idVerification) {
        return redirect(PATHS.verifyId);
      }
    }

    return fetchUser ? { user } : null;
  };
};

/**
 * Pre-configured loaders for common use cases.
 * These maintain backward compatibility with existing route definitions.
 */

// Basic auth - just requires valid token
export const authLoader = createAuthLoader({
  requireAuth: true,
  requireVerification: false,
  requireRole: null,
});

// Requires auth + the app's ID gate: a verified ID, or one Stripe is still
// reviewing (Tyler, 2026-10-22)
export const verificationLoader = createAuthLoader({
  requireAuth: true,
  requireIdGate: true,
  requireRole: null,
});

// Requires auth + a verified ID: checkout and the pages that wait for the
// verdict, where a check still in review is not enough
export const verifiedIdLoader = createAuthLoader({
  requireAuth: true,
  requireVerification: true,
  requireRole: null,
});

// Requires auth + ID verification + vendor role
export const vendorLoader = createAuthLoader({
  requireAuth: true,
  requireVerification: true,
  requireRole: 'vendor',
});

// Verify-ID page - requires auth, redirects away if already verified
export const verifyIdLoader = async ({ request } = {}) => {
  const isValid = await ensureValidToken();
  if (!isValid) {
    return redirect(loginWithReturnTo(request));
  }

  const user = await fetchUserProfile();
  if (user?.verification?.idVerification) {
    return redirect('/');
  }

  return { user };
};

// Guest-only pages (redirects if logged in)
export const guestLoader = createAuthLoader({
  requireAuth: false,
  guestOnly: true,
  fetchUser: false,
});
